import { useState } from "react";
import { Stack, Typography } from "@mui/material";
import MetricValueCard from "../MetricValueCard";
import { getWellnessFlagIconProps } from "../wellnessFlagIconProps";
import { WellnessScoreStatus } from "../types";
import IconLoader from "@/features/Common/components/IconLoader";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import VitalUpdateModal from "../VitalUpdateModal";
import { trackEvent } from "@/services/analytics-adapter";
import { navigateToBinahScan } from "@/utils";

export interface MetricCardConfig {
  translationPrefix: string;
  analyticsSource: string;
  manualEntryRoute: string;
  ctaRoute?: string;
}

export interface BaseMetricCardProps {
  metricValue: string;
  status: WellnessScoreStatus;
  lastUpdated?: string;
  unit?: string;
  config: MetricCardConfig;
  onManualEntryClick?: () => void;
}

export default function BaseMetricCard({
  metricValue,
  status,
  lastUpdated,
  unit: unitOverride,
  config,
  onManualEntryClick,
}: BaseMetricCardProps) {
  const { translationPrefix, analyticsSource, manualEntryRoute, ctaRoute } =
    config;
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleCtaClick = () => {
    if (ctaRoute) {
      navigate(ctaRoute);
    } else {
      setIsModalOpen(true);
    }
  };

  const handleFaceScan = () => {
    setIsModalOpen(false);
    trackEvent({
      event: "action.healthCheckManuallyInitiated",
      source: analyticsSource,
    });
    navigateToBinahScan("/binah-scan?skipScanPrompt=true");
  };

  const handleManualEntry = () => {
    setIsModalOpen(false);
    if (onManualEntryClick) {
      onManualEntryClick();
    } else {
      navigate(manualEntryRoute);
    }
  };

  const renderStatusMessage = () => {
    const messageKey =
      status === WellnessScoreStatus.NeedsAttention
        ? `${translationPrefix}.message.needsAttention`
        : status === WellnessScoreStatus.NeedsSupport
          ? `${translationPrefix}.message.needsSupport`
          : null;

    if (!messageKey) {
      return null;
    }

    const isNeedsSupport = status === WellnessScoreStatus.NeedsSupport;

    return (
      <Stack
        sx={{
          backgroundColor: isNeedsSupport ? "error.light" : "secondary.100",
          borderRadius: 2,
          p: 2,
        }}
        flexDirection="row"
        gap={1.5}
      >
        <IconLoader icon="FlagIcon" {...getWellnessFlagIconProps(status)} />
        <Typography
          sx={{
            fontSize: 14,
            lineHeight: 1.5,
            color: "neutral.700",
          }}
        >
          {t(messageKey as any)}
        </Typography>
      </Stack>
    );
  };

  return (
    <>
      <MetricValueCard
        metricName={t(`${translationPrefix}.title` as any)}
        metricValue={metricValue}
        unit={unitOverride ?? t(`${translationPrefix}.unit` as any)}
        status={status}
        lastUpdated={lastUpdated}
        ctaText={t(`${translationPrefix}.cta` as any)}
        onCtaClick={handleCtaClick}
      >
        {renderStatusMessage()}
      </MetricValueCard>

      <VitalUpdateModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        icon="VitalsIcon"
        title={t(`${translationPrefix}.modal.title` as any)}
        description={t(`${translationPrefix}.modal.description` as any)}
        primaryButtonText={t(`${translationPrefix}.modal.primaryButton` as any)}
        primaryButtonIcon="SmilingFaceInRoundedSquareIcon"
        secondaryButtonText={t(
          `${translationPrefix}.modal.secondaryButton` as any
        )}
        onPrimaryClick={handleFaceScan}
        onSecondaryClick={handleManualEntry}
      />
    </>
  );
}
